// AGC: feedbackService - Unified click feedback (sound + haptics) for DroidFarm
// AGC: Uses Web Audio API for sound, Capacitor Haptics on native; single toggle controls both.
// AGC: Includes 40ms throttle to prevent double-fires and iOS AudioContext unlock support.

import { Capacitor } from '@capacitor/core'

const FEEDBACK_KEY = 'droidfarm-click-feedback'
const THROTTLE_MS = 40

let audioCtx: AudioContext | null = null
let lastPlayTime = 0

/**
 * AGC: Unlock AudioContext for iOS/Safari - must be called after user gesture.
 * AGC: Add one-time pointerdown listener in main.tsx so first real click can play sound.
 */
export function unlockAudioContext(): void {
  if (typeof window === 'undefined') return
  if (!audioCtx) {
    const Ctx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (Ctx) {
      audioCtx = new Ctx()
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume()
  }
}

/**
 * AGC: Check if click feedback (sound + haptics) is enabled.
 */
export function isFeedbackEnabled(): boolean {
  try {
    return localStorage.getItem(FEEDBACK_KEY) !== 'false'
  } catch {
    return true
  }
}

/**
 * AGC: Set click feedback enabled state (persists to localStorage).
 */
export function setFeedbackEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(FEEDBACK_KEY, String(enabled))
  } catch {
    // ignore
  }
}

/**
 * AGC: Play a short synthesized click via Web Audio API.
 * AGC: 800Hz sine, 60ms decay - works on all platforms.
 */
function playWebAudioClick(): void {
  if (typeof window === 'undefined') return
  if (!audioCtx) {
    const Ctx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (Ctx) audioCtx = new Ctx()
  }
  const ctx = audioCtx
  if (!ctx) return

  try {
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 800
    osc.type = 'sine'
    gain.gain.setValueAtTime(0.12, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.06)
  } catch {
    // ignore
  }
}

/**
 * AGC: Play click feedback - sound and/or haptics.
 * AGC: On native: Haptics.impact(Light) + WebAudio. On web: WebAudio only.
 * AGC: Throttled to 40ms to avoid double-fires.
 */
export function playFeedback(): void {
  if (!isFeedbackEnabled()) return

  const now = performance.now()
  if (now - lastPlayTime < THROTTLE_MS) return
  lastPlayTime = now

  // AGC: Native haptics (fire-and-forget)
  if (Capacitor.isNativePlatform()) {
    import('@capacitor/haptics')
      .then(({ Haptics, ImpactStyle }) => {
        Haptics.impact({ style: ImpactStyle.Light })
      })
      .catch(() => {
        // Plugin not available, fallback to audio only
      })
  }

  // AGC: Web audio click (works on all platforms)
  playWebAudioClick()
}
